import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { AlertTriangle, ArrowLeft, ArrowRight, Check, Loader2, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const WORKER_ROLES = ['Operator', 'Helper', 'Supervisor', 'Technician', 'QC'];

export default function JobCorrectionDialog({
  open,
  onOpenChange,
  workOrder,
  process,
  job,
  employees = [],
  jobbers = [],
  onSuccess,
  getAuthHeader
}) {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [impactPreview, setImpactPreview] = useState(null);
  
  // Form data for Step 2
  const [formData, setFormData] = useState({
    new_quantity: 0,
    is_outsourced: false,
    jobber_id: '',
    jobber_name: '',
    workers: [{ worker_id: '', worker_name: '', role: 'Operator' }],
    machine: '',
    correction_reason: ''
  });

  // Reset on open/close
  useEffect(() => {
    if (open && workOrder && job) {
      setStep(1);
      setPreviewData(null);
      setImpactPreview(null);
      fetchPreviewData();
    }
  }, [open, workOrder?.id, job?.id]);

  const fetchPreviewData = async () => {
    if (!workOrder?.id || !job?.id) return;
    
    setLoading(true);
    try {
      const res = await axios.get(
        `${API_URL}/work-orders/${workOrder.id}/jobs/${job.id}/correct-preview`,
        { headers: getAuthHeader() }
      );
      setPreviewData(res.data);
      
      // Initialize form with current values
      setFormData({
        new_quantity: res.data.current_state.quantity_issued,
        is_outsourced: res.data.current_state.is_outsourced || false,
        jobber_id: '',
        jobber_name: '',
        workers: res.data.current_state.workers?.length > 0 
          ? res.data.current_state.workers.map(w => ({
              worker_id: w.worker_id || '',
              worker_name: w.worker_name || '',
              role: w.role || 'Operator'
            }))
          : [{ worker_id: '', worker_name: '', role: 'Operator' }],
        machine: res.data.current_state.machine || '',
        correction_reason: ''
      });
    } catch (err) {
      console.error('Failed to fetch preview:', err);
      toast.error(err.response?.data?.detail || 'Failed to load job details');
      onOpenChange(false);
    } finally {
      setLoading(false);
    }
  };

  const calculateImpactPreview = () => {
    if (!previewData) return null;
    
    const { current_state, materials } = previewData;
    const oldQty = current_state.quantity_issued;
    const completedQty = current_state.quantity_completed;
    const newQty = formData.new_quantity;
    const remainingQty = oldQty - completedQty;
    const newJcQty = newQty - completedQty;
    
    // Calculate material changes
    const materialChanges = materials.map(mat => {
      const returned = mat.quantity_returnable;
      // Calculate new issue quantity based on BOM ratio
      const bomRatio = mat.quantity_issued / oldQty;
      const newIssueQty = newJcQty > 0 ? Math.round(newJcQty * bomRatio * 10000) / 10000 : 0;
      const netChange = returned - newIssueQty;
      
      return {
        ...mat,
        quantity_returned: returned,
        quantity_new_issue: newIssueQty,
        net_change: netChange
      };
    });
    
    return {
      old_jc: {
        quantity_issued: oldQty,
        quantity_completed: completedQty,
        quantity_cancelled: remainingQty
      },
      new_jc: newJcQty > 0 ? {
        quantity_to_issue: newJcQty
      } : null,
      materials: materialChanges
    };
  };

  const handleNextStep = () => {
    if (step === 1) {
      setStep(2);
    } else if (step === 2) {
      // Validate form
      if (!formData.correction_reason.trim()) {
        toast.error('Please provide a correction reason');
        return;
      }
      if (formData.new_quantity < previewData.min_new_quantity) {
        toast.error(`New quantity cannot be less than ${previewData.min_new_quantity} (already completed)`);
        return;
      }
      
      // Calculate impact preview
      setImpactPreview(calculateImpactPreview());
      setStep(3);
    }
  };

  const handlePrevStep = () => {
    if (step > 1) setStep(step - 1);
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const payload = {
        new_quantity: formData.new_quantity,
        is_outsourced: formData.is_outsourced,
        jobber_id: formData.is_outsourced ? formData.jobber_id : null,
        jobber_name: formData.is_outsourced ? formData.jobber_name : null,
        workers: formData.workers.filter(w => w.worker_id),
        machine: formData.machine,
        correction_reason: formData.correction_reason
      };
      
      const res = await axios.post(
        `${API_URL}/work-orders/${workOrder.id}/jobs/${job.id}/correct`,
        payload,
        { headers: getAuthHeader() }
      );
      
      toast.success(res.data.message || 'Job corrected successfully');
      onSuccess?.();
      onOpenChange(false);
    } catch (err) {
      console.error('Correction failed:', err);
      toast.error(err.response?.data?.detail || 'Failed to correct job');
    } finally {
      setLoading(false);
    }
  };

  const addWorker = () => {
    if (formData.workers.length >= 5) {
      toast.error('Maximum 5 workers allowed');
      return;
    }
    setFormData(f => ({
      ...f,
      workers: [...f.workers, { worker_id: '', worker_name: '', role: 'Operator' }]
    }));
  };

  const removeWorker = (idx) => {
    setFormData(f => ({
      ...f,
      workers: f.workers.filter((_, i) => i !== idx)
    }));
  };

  const updateWorker = (idx, field, value) => {
    setFormData(f => {
      const newWorkers = [...f.workers];
      newWorkers[idx] = { ...newWorkers[idx], [field]: value };
      
      // If worker_id changed, update worker_name
      if (field === 'worker_id') {
        const emp = employees.find(e => e.id === value);
        newWorkers[idx].worker_name = emp?.name || '';
      }
      
      return { ...f, workers: newWorkers };
    });
  };

  const formatNumber = (num) => {
    if (num === undefined || num === null) return '0';
    return Number(num).toLocaleString('en-IN', { maximumFractionDigits: 2 });
  };

  if (!open) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            Correct Job Card: {job?.job_number || `Job ${job?.job_letter}`}
            <Badge variant="outline" className="ml-2">Step {step} of 3</Badge>
          </DialogTitle>
        </DialogHeader>

        {loading && !previewData ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <>
            {/* STEP 1: Current State Summary */}
            {step === 1 && previewData && (
              <div className="space-y-4">
                <Card className="border-blue-200 bg-blue-50/30">
                  <CardContent className="pt-4">
                    <h3 className="font-semibold text-blue-900 mb-3">Current State</h3>
                    <div className="grid grid-cols-3 gap-4 text-sm">
                      <div>
                        <p className="text-muted-foreground">Quantity Issued</p>
                        <p className="text-lg font-semibold">{formatNumber(previewData.current_state.quantity_issued)}</p>
                      </div>
                      <div>
                        <p className="text-muted-foreground">Quantity Completed</p>
                        <p className="text-lg font-semibold text-green-600">
                          {formatNumber(previewData.current_state.quantity_completed)}
                          {previewData.current_state.quantity_completed > 0 && (
                            <span className="text-xs ml-1 text-amber-600">Cannot be undone</span>
                          )}
                        </p>
                      </div>
                      <div>
                        <p className="text-muted-foreground">Quantity Remaining</p>
                        <p className="text-lg font-semibold text-amber-600">
                          {formatNumber(previewData.current_state.quantity_remaining)}
                          <span className="text-xs ml-1">Will be cancelled</span>
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="pt-4">
                    <h3 className="font-semibold mb-3">Materials Issued</h3>
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Material</TableHead>
                          <TableHead className="text-right">Issued</TableHead>
                          <TableHead className="text-right">Consumed</TableHead>
                          <TableHead className="text-right">Will Return</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {previewData.materials.map((mat, idx) => (
                          <TableRow key={idx}>
                            <TableCell className="font-medium">{mat.item_name}</TableCell>
                            <TableCell className="text-right">{formatNumber(mat.quantity_issued)} {mat.unit}</TableCell>
                            <TableCell className="text-right text-muted-foreground">
                              {formatNumber(mat.quantity_consumed)} {mat.unit}
                            </TableCell>
                            <TableCell className="text-right text-green-600 font-medium">
                              +{formatNumber(mat.quantity_returnable)} {mat.unit}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                    <p className="text-xs text-muted-foreground mt-2">
                      Consumed = Issued x (Completed / Issued) — proportional consumption
                    </p>
                  </CardContent>
                </Card>

                {previewData.current_state.quantity_completed === 0 && (
                  <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                    <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-amber-800">
                      No production completed. Full cancellation and re-issue will be done.
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* STEP 2: Enter New Values */}
            {step === 2 && previewData && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>New Quantity to Issue *</Label>
                    <Input
                      type="number"
                      value={formData.new_quantity}
                      onChange={(e) => setFormData(f => ({ ...f, new_quantity: parseFloat(e.target.value) || 0 }))}
                      min={previewData.min_new_quantity}
                      data-testid="correct-new-quantity"
                    />
                    <p className="text-xs text-muted-foreground">
                      Minimum: {previewData.min_new_quantity} (cannot be less than already completed)
                    </p>
                  </div>
                  <div className="space-y-2">
                    <Label>Machine</Label>
                    <Input
                      value={formData.machine}
                      onChange={(e) => setFormData(f => ({ ...f, machine: e.target.value }))}
                      placeholder="Enter machine"
                      data-testid="correct-machine"
                    />
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <Checkbox
                    id="is-outsourced"
                    checked={formData.is_outsourced}
                    onCheckedChange={(checked) => setFormData(f => ({ ...f, is_outsourced: checked }))}
                  />
                  <Label htmlFor="is-outsourced">Send to OJC (Outsourced Job Card)</Label>
                </div>

                {formData.is_outsourced && (
                  <div className="grid grid-cols-2 gap-4 pl-6 border-l-2 border-purple-200">
                    <div className="space-y-2">
                      <Label>Jobber</Label>
                      <Select
                        value={formData.jobber_id}
                        onValueChange={(v) => {
                          const jobber = jobbers.find(j => j.id === v);
                          setFormData(f => ({ ...f, jobber_id: v, jobber_name: jobber?.name || '' }));
                        }}
                      >
                        <SelectTrigger data-testid="correct-jobber">
                          <SelectValue placeholder="Select jobber" />
                        </SelectTrigger>
                        <SelectContent>
                          {jobbers.map(j => (
                            <SelectItem key={j.id} value={j.id}>{j.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                )}

                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label className="font-semibold">Workers</Label>
                    <Button type="button" size="sm" variant="outline" onClick={addWorker}>
                      <Plus className="w-3 h-3 mr-1" /> Add
                    </Button>
                  </div>
                  {formData.workers.map((w, idx) => (
                    <div key={idx} className="flex items-center gap-2">
                      <Select
                        value={w.worker_id}
                        onValueChange={(v) => updateWorker(idx, 'worker_id', v)}
                      >
                        <SelectTrigger className="flex-1">
                          <SelectValue placeholder="Select worker" />
                        </SelectTrigger>
                        <SelectContent>
                          {employees.map(e => (
                            <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Select
                        value={w.role}
                        onValueChange={(v) => updateWorker(idx, 'role', v)}
                      >
                        <SelectTrigger className="w-32">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {WORKER_ROLES.map(r => (
                            <SelectItem key={r} value={r}>{r}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {formData.workers.length > 1 && (
                        <Button type="button" size="sm" variant="ghost" onClick={() => removeWorker(idx)}>
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </Button>
                      )}
                    </div>
                  ))}
                </div>

                <div className="space-y-2">
                  <Label>Correction Reason *</Label>
                  <Textarea
                    value={formData.correction_reason}
                    onChange={(e) => setFormData(f => ({ ...f, correction_reason: e.target.value }))}
                    placeholder="Why is this job card being corrected?"
                    rows={3}
                    data-testid="correct-reason"
                  />
                </div>
              </div>
            )}

            {/* STEP 3: Impact Preview */}
            {step === 3 && impactPreview && (
              <div className="space-y-4">
                <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                  <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-amber-800 font-medium">
                    The following changes will be made:
                  </p>
                </div>

                <Card className="border-red-200">
                  <CardContent className="pt-4">
                    <h3 className="font-semibold text-red-700 mb-2">
                      Old JC ({job?.job_number || `Job ${job?.job_letter}`})
                    </h3>
                    <ul className="text-sm space-y-1 text-muted-foreground">
                      <li>Status will change to: <Badge variant="outline">Corrected</Badge></li>
                      <li>Remaining {formatNumber(impactPreview.old_jc.quantity_cancelled)} units will be cancelled</li>
                    </ul>
                  </CardContent>
                </Card>

                {impactPreview.new_jc && (
                  <Card className="border-green-200">
                    <CardContent className="pt-4">
                      <h3 className="font-semibold text-green-700 mb-2">New JC (will be created)</h3>
                      <ul className="text-sm space-y-1 text-muted-foreground">
                        <li>Quantity to issue: <span className="font-medium text-foreground">{formatNumber(impactPreview.new_jc.quantity_to_issue)}</span></li>
                        <li>New total: {formatNumber(formData.new_quantity)} ({formatNumber(previewData.current_state.quantity_completed)} completed + {formatNumber(impactPreview.new_jc.quantity_to_issue)} new)</li>
                      </ul>
                    </CardContent>
                  </Card>
                )}

                <Card>
                  <CardContent className="pt-4">
                    <h3 className="font-semibold mb-3">Net Impact on Stock</h3>
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Material</TableHead>
                          <TableHead className="text-right">Returned</TableHead>
                          <TableHead className="text-right">Re-issued</TableHead>
                          <TableHead className="text-right">Net</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {impactPreview.materials.map((mat, idx) => (
                          <TableRow key={idx}>
                            <TableCell className="font-medium">{mat.item_name}</TableCell>
                            <TableCell className="text-right text-green-600">+{formatNumber(mat.quantity_returned)}</TableCell>
                            <TableCell className="text-right text-red-600">-{formatNumber(mat.quantity_new_issue)}</TableCell>
                            <TableCell className={`text-right font-medium ${mat.net_change >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                              {mat.net_change >= 0 ? '+' : ''}{formatNumber(mat.net_change)} {mat.unit}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>

                <div className="p-3 bg-slate-50 rounded-lg">
                  <p className="text-sm">
                    <span className="font-medium">Correction Reason:</span> {formData.correction_reason}
                  </p>
                </div>
              </div>
            )}
          </>
        )}

        <DialogFooter className="flex justify-between">
          <div>
            {step > 1 && (
              <Button variant="outline" onClick={handlePrevStep} disabled={loading}>
                <ArrowLeft className="w-4 h-4 mr-1" /> Back
              </Button>
            )}
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)} disabled={loading}>
              Cancel
            </Button>
            {step < 3 ? (
              <Button onClick={handleNextStep} disabled={loading || !previewData}>
                Next <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            ) : (
              <Button 
                onClick={handleSubmit} 
                disabled={loading}
                className="bg-amber-600 hover:bg-amber-700"
                data-testid="confirm-correction-btn"
              >
                {loading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Check className="w-4 h-4 mr-1" />}
                Confirm Correction
              </Button>
            )}
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
